import React, { useState } from 'react';
import axios from 'axios';
import SponsorProductCampaign from './SponsorProductCampaign';
import SponsorShopCampaign from './SponsorShopCampaign';
import SponsorBrandCampaign from './SponsorBrandCampaign';
import SponsorCommunityCampaign from './SponsorCommunityCampaign';
import CreateCustomAdCampaign from './CreateCustomAdCampaign';
import CreateAdSteps from './CreateAdSteps';
import CampaignReview from './CampaignReview';
import CampaignTotals from './CampaignTotals';
import Loader from '../Layout/Loader';
import { server } from '../../server';
import { getStoredAuthToken } from '../../utils/authSession';
import { toast } from 'react-toastify';
import { FiPackage, FiShoppingCart, FiTag, FiSettings, FiPlus, FiUsers } from 'react-icons/fi';

const AdminCampaignCreator = ({ adminOptions = {}, onCampaignCreated }) => {
    const [activeStep, setActiveStep] = useState(1);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [campaignAds, setCampaignAds] = useState({});
    const [openAdComponents, setOpenAdComponents] = useState({});
    const [editingAd, setEditingAd] = useState(null);
    const [selectedComponent, setSelectedComponent] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const adOptions = [
        { label: 'Product', component: <SponsorProductCampaign />, type: 'product', icon: <FiPackage /> },
        { label: 'Shop', component: <SponsorShopCampaign />, type: 'shop', icon: <FiShoppingCart /> },
        { label: 'Brand', component: <SponsorBrandCampaign />, type: 'brand', icon: <FiTag /> },
        { label: 'Community', component: <SponsorCommunityCampaign />, type: 'community', icon: <FiUsers /> },
        { label: 'Custom', component: <CreateCustomAdCampaign />, type: 'custom', icon: <FiSettings /> },
    ];

    const selectedAdCount = Object.values(campaignAds).flat().length;

    const toggleOption = (option) => {
        setSelectedOptions((currentOptions) =>
            currentOptions.includes(option)
                ? currentOptions.filter((item) => item !== option)
                : [...currentOptions, option]
        );
    };

    const proceedToNextStep = () => {
        if (activeStep === 1 && selectedOptions.length === 0) {
            alert('Please select at least one ad type.');
            return;
        }

        if (activeStep === 2 && selectedAdCount === 0) {
            alert('Please save at least one ad configuration before proceeding.');
            return;
        }

        if (activeStep === 1) {
            const initialOpenComponents = {};
            selectedOptions.forEach((option) => {
                const adType = adOptions.find((ad) => ad.label === option)?.type;
                if (adType) {
                    initialOpenComponents[adType] = 1;
                }
            });
            setOpenAdComponents(initialOpenComponents);

            const firstSelectedOption = adOptions.find((ad) => ad.label === selectedOptions[0]);
            setSelectedComponent(firstSelectedOption?.type || null);
        }

        setActiveStep((currentStep) => Math.min(currentStep + 1, 4));
    };

    const goToPreviousStep = () => {
        setActiveStep((currentStep) => Math.max(currentStep - 1, 1));
    };

    const handleAdSetSave = (newAdSet, details, adType) => {
        setCampaignAds((currentAds) => {
            const existingAds = currentAds[adType] || [];
            const adDetails = {
                type: adType,
                details,
                adSet: {
                    images: newAdSet.images || {},
                    files: newAdSet.files || {},
                    positions: newAdSet.positions || {},
                    scales: newAdSet.scales || {},
                },
            };

            if (editingAd && editingAd.adType === adType) {
                const updatedAds = [...existingAds];
                updatedAds[editingAd.index] = adDetails;
                return {
                    ...currentAds,
                    [adType]: updatedAds,
                };
            }

            return {
                ...currentAds,
                [adType]: [...existingAds, adDetails],
            };
        });

        setOpenAdComponents((currentComponents) => ({
            ...currentComponents,
            [adType]: Math.max((currentComponents[adType] || 1) - 1, 0),
        }));
        setEditingAd(null);
    };

    const handleEditClick = (adType, index) => {
        const adToEdit = campaignAds[adType]?.[index];
        if (!adToEdit) {
            return;
        }

        setEditingAd({
            adType,
            index,
            details: adToEdit.details || {},
        });
        setOpenAdComponents((currentComponents) => ({
            ...currentComponents,
            [adType]: 1,
        }));
    };

    const handleAddAnother = (adType) => {
        setOpenAdComponents((currentComponents) => ({
            ...currentComponents,
            [adType]: (currentComponents[adType] || 0) + 1,
        }));
    };

    const handleIconClick = (type) => {
        setSelectedComponent(type);
    };

    const handleSubmitCampaign = async () => {
        setIsLoading(true);

        try {
            const token = getStoredAuthToken();
            const adsArray = Object.values(campaignAds).flat();
            const formData = new FormData();

            adsArray.forEach((ad, index) => {
                formData.append(`ads[${index}][type]`, ad.type);
                formData.append(`ads[${index}][details]`, JSON.stringify(ad.details || {}));

                Object.entries(ad.adSet?.images || {}).forEach(([format, imageOrFile]) => {
                    if (ad.adSet?.files?.[format] instanceof File) {
                        formData.append(`ads[${index}][adSet][images][${format}]`, ad.adSet.files[format]);
                    } else if (typeof imageOrFile === 'string') {
                        formData.append(`ads[${index}][adSet][images][${format}]`, imageOrFile);
                    }
                });

                formData.append(`ads[${index}][adSet][positions]`, JSON.stringify(ad.adSet?.positions || {}));
                formData.append(`ads[${index}][adSet][scales]`, JSON.stringify(ad.adSet?.scales || {}));
            });

            const response = await axios.post(`${server}/sponsored/admin-create-campaign`, formData, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'multipart/form-data',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
            });

            toast.success('Admin ad campaign created successfully!');
            if (typeof onCampaignCreated === 'function') {
                onCampaignCreated(response.data);
            }

            setCampaignAds({});
            setSelectedOptions([]);
            setOpenAdComponents({});
            setEditingAd(null);
            setSelectedComponent(null);
            setActiveStep(1);
        } catch (error) {
            console.error('Error creating admin campaign:', error);
            toast.error(error.response?.data?.message || 'Error creating admin campaign');
        } finally {
            setIsLoading(false);
        }
    };

    const totalAds = Object.values(campaignAds).flat().length;
    const totalDuration = Object.values(campaignAds).flat().reduce((total, ad) => total + (Number(ad.details?.adDuration) || 0), 0);
    const totalAdSpend = Object.values(campaignAds).flat().reduce((total, ad) => total + (Number(ad.details?.adSpend) || 0), 0);

    if (isLoading) {
        return <Loader />;
    }

    return (
        <div className="shop-create-advertisement-campaign-container" data-admin-campaign-creator="true">
            <div className="shop-create-advertisement-campaign-inner-container">
                <div className="shop-create-advertisement-campaign-box-modal">
                    <CreateAdSteps activeStep={activeStep} finalStepLabel="Create Ad" />
                    <h2 className="shop-create-advertisement-campaign-title">
                        {activeStep === 1 && 'Choose Your Ad Type'}
                        {activeStep === 2 && 'Customize Your Ads'}
                        {activeStep === 3 && 'Review Your Ads'}
                        {activeStep === 4 && 'Create Ad'}
                    </h2>

                    {activeStep === 1 && (
                        <div className="shop-create-advertisement-campaign-options-row">
                            {adOptions.map((option, index) => (
                                <div key={option.type} className="shop-create-advertisement-campaign-option-item">
                                    <div className="shop-create-advertisement-campaign-icon-label-container">
                                        <div className="shop-create-advertisement-campaign-option-icon">
                                            {option.icon}
                                        </div>
                                        <span className="shop-create-advertisement-campaign-option-label">{option.label}</span>
                                    </div>
                                    <div className="switch large">
                                        <input
                                            className="switch-input"
                                            id={`admin-option-toggle-${index}`}
                                            type="checkbox"
                                            checked={selectedOptions.includes(option.label)}
                                            onChange={() => toggleOption(option.label)}
                                        />
                                        <label className="switch-paddle" htmlFor={`admin-option-toggle-${index}`}></label>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {activeStep === 2 && (
                        <div className="shop-create-advertisement-campaign-expanded-sections">
                            <div className="selected-options-display">
                                {selectedOptions.map((optionLabel) => {
                                    const option = adOptions.find((ad) => ad.label === optionLabel);
                                    if (!option) {
                                        return null;
                                    }

                                    return (
                                        <div
                                            key={option.type}
                                            className={`shop-create-advertisement-campaign-option-item ${selectedComponent === option.type ? 'selected' : ''}`}
                                            onClick={() => handleIconClick(option.type)}
                                        >
                                            <div className="shop-create-advertisement-campaign-icon-label-container">
                                                <div className="shop-create-advertisement-campaign-option-icon">
                                                    {option.icon}
                                                </div>
                                                <span className="shop-create-advertisement-campaign-option-label">
                                                    {option.label}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {adOptions.map((option) => {
                                if (selectedComponent !== option.type) {
                                    return null;
                                }

                                const savedConfigs = campaignAds[option.type] || [];

                                return (
                                    <div key={option.type} className="ad-config-section">
                                        {savedConfigs.map((config, index) => (
                                            editingAd?.adType !== option.type || editingAd?.index !== index ? (
                                                <div key={`${option.type}-${index}`} className="ad-overview-row">
                                                    <div className="ad-overview-content">
                                                        <div className="ad-overview-details">
                                                            <h4>{`${option.label} Overview ${index + 1}`}</h4>
                                                            <p><strong>Ad Spend:</strong> ${config.details.adSpend}</p>
                                                            <p><strong>Duration:</strong> {config.details.adDuration} days</p>
                                                            {config.details.productName && <p><strong>Product:</strong> {config.details.productName}</p>}
                                                            {config.details.shopName && <p><strong>Shop:</strong> {config.details.shopName}</p>}
                                                            {config.details.brandName && <p><strong>Brand:</strong> {config.details.brandName}</p>}
                                                            {config.details.communityName && <p><strong>Community:</strong> {config.details.communityName}</p>}
                                                            {config.details.audienceScope && option.type === 'community' && <p><strong>Audience:</strong> {config.details.audienceScope}</p>}
                                                            {config.details.adText && <p><strong>Ad Text:</strong> {config.details.adText}</p>}
                                                            {config.details.adLink && <p><strong>Link:</strong> {config.details.adLink}</p>}
                                                            <button type="button" onClick={() => handleEditClick(option.type, index)}>
                                                                Edit
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : null
                                        ))}

                                        {[...Array(openAdComponents[option.type] || 0)].map((_, index) => (
                                            <div key={`${option.type}-new-${index}`} className="new-ad-config">
                                                {React.cloneElement(option.component, {
                                                    onSave: handleAdSetSave,
                                                    adType: option.type,
                                                    details: editingAd?.details || {},
                                                    adminMode: true,
                                                    adminOptions,
                                                })}
                                            </div>
                                        ))}

                                        {savedConfigs.length > 0 && (
                                            <button
                                                type="button"
                                                className="add-new-ad-button"
                                                onClick={() => handleAddAnother(option.type)}
                                            >
                                                <FiPlus style={{ marginRight: '8px' }} />
                                                Add Another {option.label}
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {activeStep === 3 && (
                        <div className="shop-create-advertisement-review-section">
                            <CampaignReview
                                campaignAds={campaignAds}
                                onEdit={(adType, index) => {
                                    setActiveStep(2);
                                    setSelectedComponent(adType);
                                    handleEditClick(adType, index);
                                }}
                            />
                        </div>
                    )}

                    {activeStep === 4 && (
                        <div className="shop-create-advertisement-checkout-section">
                            <h3>Create Ad</h3>
                            <div className="shop-create-advertisement-checkout-summary">
                                <p><strong>Total Ads:</strong> {totalAds}</p>
                                <p><strong>Total Duration:</strong> {totalDuration} days</p>
                                <p><strong>Total Ad Spend:</strong> ${totalAdSpend}</p>
                            </div>
                            <button
                                className="create-ad-button create-ad-complete-button"
                                onClick={handleSubmitCampaign}
                                type="button"
                            >
                                Create Ad
                            </button>
                        </div>
                    )}

                    <div className="create-ad-button-group">
                        {activeStep > 1 && (
                            <button
                                className="create-ad-previous-button"
                                onClick={goToPreviousStep}
                                type="button"
                            >
                                Previous Step
                            </button>
                        )}
                        {activeStep < 4 && (
                            <button
                                className={`create-ad-next-button ${activeStep === 2 && selectedAdCount === 0 ? 'disabled' : ''}`}
                                onClick={proceedToNextStep}
                                disabled={activeStep === 2 && selectedAdCount === 0}
                                type="button"
                            >
                                Next Step
                            </button>
                        )}
                    </div>
                </div>

                <CampaignTotals campaignAds={Object.values(campaignAds).flat()} />
            </div>
        </div>
    );
};

export default AdminCampaignCreator;